/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { deleteItemByIdCfn } from '../shared.functions';
import { groupAppliancesCfn } from './functions/group-appliances.cfn';
import { Obstruction } from './obstruction.class';
import { obstructionsData } from './obstructions.data';
import { Publish } from '../../decorators';
import { updateIslandPositionsCfn } from './functions';
import { Vertex3 } from '../../spatial-classes';

import type { Size } from '../../types';

import { CornerSize, GroupAppliances, ObstructionsDataIn, ObstructionsDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { ObstructionType } from './enums';

export class Obstructions extends KitchenDesignBase<ObstructionsDataIn> {
	#defaultIslandId: string;
	#tempOrigin: Vertex3;

	readonly children: Array<Obstruction>;

	constructor (options: KitchenDesignOptions<ObstructionsDataIn> = {}) {
		super(BlueprintInstanceType.Obstructions, options);

		const { dataIn = null } = options;
		const data = obstructionsData(dataIn)
		this.children = [];

		this.#defaultIslandId = data.defaultIslandId;
		this.#tempOrigin = null;

		if (data.children.length > 0) {
			data.children.forEach((n) => this.children.push(new Obstruction({ dataIn: n, parent: this })));
		}

		this.setInitialized();

		this.refMap.kitchen?.subscribe('surround_material', 'updated', () => this.updateIslandPositions());
	}

	/**
	 * @param orientation axis.y value of the islands
	 * @returns array of islands matching the orientation (angle) parameter
	 */
	public getIslandsByOrientation (orientation: number): Array<Obstruction> {
		return this.children.filter((island) => island.axis.y === orientation);
	}

	/**
	 * A getter that returns the island id of the default island
	 */
	get defaultIslandId (): string { return this.#defaultIslandId }

	/**
	 * A getter that returns the number of islands
	 */
	get length (): number { return this.children.length }

	/**
	 * A getter that represents the centerpoint of default island, which is the origin of islands
	 */
	get origin (): Vertex3 {
		if (this.#tempOrigin === null) this.#tempOrigin = this.getDefaultIsland().origin as Vertex3;
		return this.#tempOrigin;
	}

	/**
	 * Delete an island
	 * @param id id of island to delete
	 */
	@Publish('islands', 'deleted')
	public delete (id: string): void {
		const island = this.findById(id);

		if (island && island.connections.children.length <= 1) {
			this.findById(id).connections.deleteSourceConnection();
			deleteItemByIdCfn(this.children, id);
		}
	}

	/**
	 * A method to group the appliance from all islands into a reportable structure
	 *
	 * @returns GroupAppliances
	 */
	public groupAppliances (): GroupAppliances {
		return groupAppliancesCfn(this as Obstructions);
	}

	/**
	 * A method for setting the default island.
	 *
	 * @param defaultIslandId The id of the island representing the new default. The island must already exist under islands.
	 * @returns true if the island exist and is set as the new default island
	 */
	@Publish('island_default', 'updated')
	public setDefaultIsland (defaultIslandId: string): boolean {
		const island = this.children.find((i) => i.id === defaultIslandId) || null;
		if (island) this.#defaultIslandId = island.id;
		return island !== null;
	}

	/**
	 * A method used to return an instance of the default Island
	 *
	 * @returns instance of Island representing the default island or NULL if default isn't set
	 * */
	public getDefaultIsland (): Obstruction | null {
		return this.children.find((i) => i.id === this.#defaultIslandId) || null;
	}

	/**
	 * Creates and adds a new island to the islands instance
	 * @param islandType IslandsType enum
	 * @param islandSize default base size of island
	 * @param sku string representing the default sku
	 * @returns Instance of island created
	 */
	public newObstruction (obstructionType: ObstructionType, obstructionSize: Size | CornerSize): Obstruction {
		const obstruction = new Obstruction({
			dataIn: {
				type: obstructionType,
				size: obstructionSize
			},
			parent: this
		});
		this.children.push(obstruction);

		// make this the default island if one doesn't exist
		if (this.#defaultIslandId === '') {
			this.setDefaultIsland(obstruction.id);
		}

		return obstruction;
	}

	/**
	 * method to allows the consumer to call find instead of having to used children.find
	 * @param callback signature matched array.find()
	 * @returns instance of Island or null
	 */
	public find (callback: (key: Obstruction) => boolean | undefined): Obstruction | null {
		return (this.children.find(callback) as Obstruction) || null;
	}

	/**
	 * finds an instance of island using the islands id
	 * @param id island id representing the island we are looking for
	 * @returns instance of the island found, or NULL if not found
	 */
	public findById (id: string): Obstruction {
		return (this.children.find((i) => i.id === id)) as Obstruction || null;
	}

	/**
	 * finds an instance of island containing a panel matching the provided panel id
	 * @param id island id representing the panel we are looking for
	 * @returns instance of the island found, or NULL if not found
	 */
	public findByPanel (): Obstruction {
		return this.children[0];
	}

	/**
	 * islands origin is based on the default island. Set a temporary value to be read when published.
	 * @param point new origin point for islands
	 * @emits islands_origin.updated
	 */
	@Publish('islands_origin', 'updated')
	public setOrigin (point: Vertex3): void {
		this.#tempOrigin = point;
	}

	/**
	 * Update the position of the islands
	 * @emits islands_positions.updated
	 */
	@Publish('islands_positions', 'updated')
	public updateIslandPositions (): void {
		updateIslandPositionsCfn(this as Obstructions);
	}

	/**
	 * @returns serialized data representing class state for persistence
	 */
	public serialize (): ObstructionsDataOut {
		return {
			defaultIslandId: this.#defaultIslandId,
			children: this.children.map((island: Obstruction) => island.serialize())
		};
	}
}

